import { type StateCreator, create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { Howl } from 'howler'
import { match } from 'ts-pattern'
import { logger } from '@loc/helpers/src/logger/client'
import { devtools } from 'zustand/middleware'

export interface AudioGlobalMetadata {
  numId: number
  id: string
  src: string
  cover: string
  musicType?: string | null
  title?: string | null
  style?: string | null
  mood?: string | null
  voice?: string | null
  lyricsPrompt?: string | null
  description?: string | null
  /** 秒为单位 */
  duration: number
  author: {
    id: string
    email?: string | null
    name?: string | null
    displayName?: string | null
    avatar?: string | null
  }
  favoriteNum?: number | null
  waveformSrc: string
  lyrics: string
  lyricsLrc: string
  createAt: Date
  isLiked?: boolean
  isSelected?: boolean
  coverMusicId?: number | null
  coverAuthor?: {
    id?: number
    name?: string
    image?: string
    description?: string
  } | null
}

export type ResourceState = 'idle' | 'loading' | 'loaded' | 'error'
export type LoopType = 'off' | 'single'

export interface AudioState {
  meta: AudioGlobalMetadata | null
  playStart?: number
  loop: LoopType
  howl: null | Howl
  /** 资源状态 */
  resourceState: ResourceState
  volume: number
  playing: boolean
  currentTimeInSeconds: number
  collapsed: boolean
  playList: AudioGlobalMetadata[]
  showDetail: boolean
}

interface Actions {
  init: <T extends AudioGlobalMetadata>(
    meta: AudioGlobalMetadata,
    playStart?: number,
    playList?: T[],
  ) => void
  play: <T extends AudioGlobalMetadata>(
    meta: AudioGlobalMetadata,
    playStart?: number,
    playList?: T[],
  ) => void
  toggleShowDetail: (showDetail?: boolean) => void
  playNext: () => void
  playPrev: () => void
  resourceStateUpdate: (state: ResourceState) => void
  volumeUpdate: (volume: number) => void
  loopToggle: () => void
  _playingUpdate: (playing: boolean) => void
  _currentTimeInSecondsUpdate: (currentTimeInSeconds: number) => void
  onTimeUpdate: (callback: (timeInSeconds: number) => void) => () => void
  seek: (timeInSeconds: number) => void
  toggle: () => void
  match: (id: string) => boolean
  matchSrc: (src?: string | null) => boolean
  toggleCollapsed: (collapsed?: boolean) => void
  unLoad: () => void
}

export type AudioSharedSlice = AudioState & Actions

const initialState: AudioState = {
  meta: null,
  loop: 'off',
  howl: null,
  resourceState: 'idle',
  volume: 1,
  playing: false,
  currentTimeInSeconds: 0,
  collapsed: true,
  playList: [],
  showDetail: false,
}

export const createSiteSharedSlice: StateCreator<
  AudioSharedSlice,
  [['zustand/immer', never]],
  [],
  AudioSharedSlice
> = (set, get) => ({
  ...initialState,
  init(meta, playStart, playList) {
    const howl = new Howl({
      src: meta.src,
      // 特殊情况下 src 可能没有 .mp3 后缀
      format: 'mp3',
      volume: get().volume,
      loop: get().loop === 'single',
      html5: true,
    })
    const handlePlay = () => {
      get()._currentTimeInSecondsUpdate(howl.seek())
      if (howl.playing()) {
        requestAnimationFrame(() => {
          handlePlay()
        })
      }
    }

    howl.on('load', () => {
      get().resourceStateUpdate('loaded')
      if (playStart) {
        howl.seek(playStart)
      }
      if (get().playing) {
        howl.play()
      }
    })
    howl.on('loaderror', (err) => {
      // eslint-disable-next-line no-console
      console.error('loadError', err)
      get().resourceStateUpdate('error')
      get()._playingUpdate(false)
    })
    howl.on('play', handlePlay)
    howl.on('end', () => {
      if (get().loop === 'off') {
        get()._playingUpdate(false)
      }
      if (get().playList.length > 1) {
        get().playNext()
      }
    })

    get().howl?.unload()

    set((state) => {
      state.meta = meta
      state.playStart = playStart
      state.currentTimeInSeconds = playStart ?? 0
      state.playing = false
      state.resourceState = 'loading'
      state.howl = howl
      state.collapsed = false
      state.playList = playList || []
    })
  },
  play(meta, playStart, playList) {
    if (get().match(meta.id)) {
      if (typeof playStart === 'undefined') {
        logger.debug('toggle', meta)
        get().toggle()
      } else {
        logger.debug('seek', meta, playStart)
        get().seek(playStart)
        if (!get().playing) {
          get().toggle()
        }
      }
      return
    }

    logger.debug('play', meta, playStart)
    get().init(meta, playStart, playList)
    set((state) => {
      state.playing = true
    })
  },
  resourceStateUpdate(resourceState) {
    set((state) => {
      state.resourceState = resourceState
    })
  },
  volumeUpdate(volume) {
    set((state) => {
      state.volume = volume
      state.howl?.volume(volume)
    })
  },
  loopToggle() {
    const types: LoopType[] = ['off', 'single']
    const currentTypeIndex = types.findIndex((item) => item === get().loop)
    const nextType = types[(currentTypeIndex + 1) % types.length]

    set((state) => {
      state.loop = nextType
      match(nextType)
        .with('single', () => {
          this.howl?.loop(true)
        })
        .otherwise(() => {
          this.howl?.loop(false)
        })
    })
  },
  _playingUpdate(playing) {
    set((state) => {
      state.playing = playing
    })
  },
  _currentTimeInSecondsUpdate(currentTimeInSeconds) {
    set((state) => {
      state.currentTimeInSeconds = currentTimeInSeconds
    })
  },
  onTimeUpdate(callback) {
    const howl = get().howl
    if (!howl) {
      return () => {}
    }
    let toggle = true
    const innerCallback = () => {
      callback(howl.seek())
      if (howl.playing() && toggle) {
        requestAnimationFrame(() => {
          innerCallback()
        })
      }
    }
    innerCallback()
    howl.on('play', innerCallback)
    return () => {
      toggle = false
      howl.off('play', innerCallback)
    }
  },
  seek(timeInSeconds) {
    get().howl?.seek(timeInSeconds)
    if (get().playing && get().howl?.playing() === false) {
      get().howl?.play()
    }
  },
  toggle() {
    if (get().resourceState !== 'loaded') {
      logger.debug('unexpected resource state', get().resourceState)
      return
    }
    const howl = get().howl
    if (!howl) {
      logger.debug('no player instance')
      return
    }
    if (get().playing) {
      howl.pause()
      get()._playingUpdate(false)
    } else {
      howl.play()
      get()._playingUpdate(true)
    }
  },
  playNext() {
    if (!get().howl) return
    const playList = get().playList
    if (playList.length <= 1) {
      get().seek(0)
      if (!get().playing) {
        get().toggle()
      }
    } else {
      if (get().playing) {
        get().toggle()
      }
      const metaId = get().meta?.id
      if (!metaId) {
        return false
      }
      const index = playList.findIndex((item) => item.id === metaId)
      if (index !== -1) {
        get().play(playList[(index + 1) % playList.length], 0, playList)
      }
    }
  },
  //播放前一首
  playPrev() {
    if (!get().howl) return
    const playList = get().playList
    if (playList.length <= 1) {
      get().seek(0)
      if (!get().playing) {
        get().toggle()
      }
    } else {
      if (get().playing) {
        get().toggle()
      }
      const metaId = get().meta?.id
      if (!metaId) {
        return false
      }
      const index = playList.findIndex((item) => item.id === metaId)
      if (index !== -1) {
        get().play(
          playList[(index - 1 + playList.length) % playList.length],
          0,
          playList,
        )
      }
    }
  },
  match(id) {
    const metaId = get().meta?.id
    if (!metaId) {
      return false
    }
    return metaId === id
  },
  matchSrc(src) {
    const metaSrc = get().meta?.src
    if (!metaSrc) {
      return false
    }
    return metaSrc === src
  },
  toggleCollapsed() {
    set((state) => {
      state.collapsed = !get().collapsed
    })
  },
  toggleShowDetail(showDetail) {
    set((state) => {
      state.showDetail =
        showDetail !== undefined ? showDetail : !get().showDetail
    })
  },
  unLoad() {
    if (get().howl) {
      get().howl?.unload()
    }
    if (get().playing) {
      get()._playingUpdate(false)
    }
    set((state) => {
      state.howl = null
      state.meta = null
    })
  },
})

export const useGlobalAudioPlayer = create(
  devtools(immer(createSiteSharedSlice)),
)
